import { ethers } from 'ethers';
import { CHAIN_NAMESPACES, WEB3AUTH_NETWORK, type IProvider } from '@web3auth/base';
import { EthereumPrivateKeyProvider } from '@web3auth/ethereum-provider';
import { Web3Auth } from '@web3auth/single-factor-auth';

/**
 * Wallet abstraction.
 *
 * Two backends:
 *  1. Web3Auth Single-Factor Auth (prod): the user's RS256 JWT (issued by
 *     `/v1/webapp/auth/exchange`, JWKS at `/.well-known/jwks.json`) is
 *     exchanged for an MPC-derived EOA. No seed phrase, no key in the clear.
 *  2. localStorage random EOA (dev fallback): used when
 *     `VITE_WEB3AUTH_CLIENT_ID` is empty so the rest of the flow works
 *     without a Web3Auth dashboard account.
 *
 * Dashboard setup (production):
 *   - https://dashboard.web3auth.io  ->  new Plug-and-Play project
 *   - Add Custom Auth verifier:
 *       Verifier id     = VITE_WEB3AUTH_VERIFIER (default: torian-telegram)
 *       Auth provider   = Custom Provider
 *       JWKS URL        = <API>/api/.well-known/jwks.json
 *       JWT user id     = sub          (we issue sub = tg:<telegram_user_id>)
 *       JWT issuer/aud  = JWT_ISSUER / JWT_AUDIENCE from the API
 */

export interface SignerHandle {
  address: string;
  signTypedData(
    domain: ethers.TypedDataDomain,
    types: Record<string, ethers.TypedDataField[]>,
    value: Record<string, unknown>,
  ): Promise<string>;
}

export interface GetSignerInput {
  jwt: string;
  subject: string;
}

const LS_KEY = 'torian.dev.eoa';
const CLIENT_ID = import.meta.env.VITE_WEB3AUTH_CLIENT_ID;
const VERIFIER = import.meta.env.VITE_WEB3AUTH_VERIFIER || 'torian-telegram-proxy';
const NETWORK =
  import.meta.env.VITE_WEB3AUTH_NETWORK === 'sapphire_mainnet' ||
  import.meta.env.VITE_WEB3AUTH_NETWORK === 'mainnet'
    ? WEB3AUTH_NETWORK.SAPPHIRE_MAINNET
    : WEB3AUTH_NETWORK.SAPPHIRE_DEVNET;

const CHAIN_CONFIG = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: '0x89',
  rpcTarget:
    import.meta.env.VITE_POLYGON_RPC_URL ||
    'https://polygon-bor-rpc.publicnode.com',
  displayName: 'Polygon Mainnet',
  blockExplorerUrl: 'https://polygonscan.com',
  ticker: 'MATIC',
  tickerName: 'MATIC',
  logo: 'https://assets.web3auth.io/evm-chains/polygon.png',
};

let cached: SignerHandle | null = null;
let web3auth: Web3Auth | null = null;
let web3authInit: Promise<void> | null = null;

export function isUsingWeb3Auth(): boolean {
  return Boolean(CLIENT_ID);
}

async function getWeb3Auth(): Promise<Web3Auth> {
  if (web3auth && web3authInit) {
    try {
      await web3authInit;
      return web3auth;
    } catch (err) {
      // Init failed previously (flaky network inside Telegram's WKWebView).
      // Drop the cached rejection so the next attempt actually re-runs init,
      // otherwise every retry resolves the same dead promise.
      web3auth = null;
      web3authInit = null;
      throw err;
    }
  }
  const privateKeyProvider = new EthereumPrivateKeyProvider({
    config: { chainConfig: CHAIN_CONFIG },
  });
  const instance = new Web3Auth({
    clientId: CLIENT_ID as string,
    web3AuthNetwork: NETWORK,
    privateKeyProvider,
  });
  const initPromise = instance.init().catch((err) => {
    if (web3auth === instance) {
      web3auth = null;
      web3authInit = null;
    }
    throw err;
  });
  web3auth = instance;
  web3authInit = initPromise;
  await initPromise;
  return instance;
}

async function signerFromProvider(provider: IProvider): Promise<SignerHandle> {
  const pk = (await provider.request({ method: 'eth_private_key' })) as string;
  const privateKey = pk.startsWith('0x') ? pk : `0x${pk}`;
  const wallet = new ethers.Wallet(privateKey);
  return {
    address: wallet.address,
    signTypedData: (domain, types, value) =>
      wallet.signTypedData(
        domain,
        types as Record<string, ethers.TypedDataField[]>,
        value,
      ),
  };
}

async function getWeb3AuthSigner(input: GetSignerInput): Promise<SignerHandle> {
  const w3a = await getWeb3Auth();
  if (!w3a.connected) {
    try {
      await w3a.connect({
        verifier: VERIFIER,
        verifierId: input.subject,
        idToken: input.jwt,
      });
    } catch (err) {
      // Wipe the cached instance so the next retry rebuilds from scratch
      // instead of inheriting whatever half-initialized state remained.
      web3auth = null;
      web3authInit = null;
      throw err;
    }
  }
  if (!w3a.provider) {
    throw new Error('Web3Auth connected but provider missing');
  }
  return signerFromProvider(w3a.provider);
}

function getDevSigner(): SignerHandle {
  let pk = localStorage.getItem(LS_KEY);
  if (!pk) {
    pk = ethers.Wallet.createRandom().privateKey;
    localStorage.setItem(LS_KEY, pk);
  }
  const wallet = new ethers.Wallet(pk);
  return {
    address: wallet.address,
    signTypedData: (domain, types, value) =>
      wallet.signTypedData(
        domain,
        types as Record<string, ethers.TypedDataField[]>,
        value,
      ),
  };
}

export async function getSigner(input?: GetSignerInput): Promise<SignerHandle> {
  if (cached) return cached;
  if (isUsingWeb3Auth()) {
    if (!input) {
      throw new Error('Web3Auth requires { jwt, subject } - call after auth exchange.');
    }
    cached = await getWeb3AuthSigner(input);
    return cached;
  }
  cached = getDevSigner();
  return cached;
}

export async function clearSigner(): Promise<void> {
  cached = null;
  if (web3auth?.connected) {
    try {
      await web3auth.logout();
    } catch {
      // ignore
    }
  }
}

/**
 * Derive Polymarket CLOB L2 API credentials by signing the EIP-712 ClobAuth
 * payload with the user's EOA. Returns { key, secret, passphrase }.
 *
 * Same algorithm used by `@polymarket/clob-client-v2#createOrDeriveApiKey`,
 * inlined here to avoid pulling the full SDK into the bundle.
 */
const CLOB_HOST = 'https://clob.polymarket.com';
const POLYGON_CHAIN_ID = 137;

export async function deriveClobCreds(
  signer: SignerHandle,
  options?: { polyAddress?: string },
): Promise<{ key: string; secret: string; passphrase: string }> {
  const polyAddress = (options?.polyAddress ?? signer.address) as string;
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = 0;
  const domain = {
    name: 'ClobAuthDomain',
    version: '1',
    chainId: POLYGON_CHAIN_ID,
  };
  const types = {
    ClobAuth: [
      { name: 'address', type: 'address' },
      { name: 'timestamp', type: 'string' },
      { name: 'nonce', type: 'uint256' },
      { name: 'message', type: 'string' },
    ],
  };
  const value = {
    address: polyAddress,
    timestamp,
    nonce,
    message: 'This message attests that I control the given wallet',
  };
  const signature = await signer.signTypedData(domain, types, value);
  const headers: Record<string, string> = {
    POLY_ADDRESS: polyAddress,
    POLY_SIGNATURE: signature,
    POLY_TIMESTAMP: timestamp,
    POLY_NONCE: String(nonce),
  };
  // Try derive first (deterministic; works whether or not the EOA already has
  // an API key). Falls back to create if derive returns nothing.
  let res = await fetch(`${CLOB_HOST}/auth/derive-api-key`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    res = await fetch(`${CLOB_HOST}/auth/api-key`, {
      method: 'POST',
      headers,
    });
  }
  if (!res.ok) {
    throw new Error(
      `CLOB auth failed: ${res.status} ${await res.text()}`,
    );
  }
  const data = (await res.json()) as {
    apiKey: string;
    secret: string;
    passphrase: string;
  };
  return { key: data.apiKey, secret: data.secret, passphrase: data.passphrase };
}
